import { useCallback, useEffect, useState } from 'react';

// Sólo esta preferencia se guarda (brief requisito 4): cuándo deja de
// suprimirse el aviso de instalación tras aceptar u ocultarlo.
export const INSTALL_SUPPRESS_KEY = 'hammer-pwa-install-dismissed-until';
const SUPPRESS_MS = 14 * 24 * 60 * 60 * 1000;

function readSuppressedUntil() {
  try {
    const raw = window.localStorage.getItem(INSTALL_SUPPRESS_KEY);
    const value = raw ? Number(raw) : 0;
    return Number.isFinite(value) ? value : 0;
  } catch {
    // Modo privado, cuota llena, localStorage deshabilitado: nunca bloquea
    // la UI, sólo se pierde la preferencia de "no molestar".
    return 0;
  }
}

function writeSuppressedUntil(timestamp) {
  try {
    window.localStorage.setItem(INSTALL_SUPPRESS_KEY, String(timestamp));
  } catch {
    // Igual que arriba: sin storage disponible, simplemente no persiste.
  }
}

function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  const media = typeof window.matchMedia === 'function'
    ? window.matchMedia('(display-mode: standalone)')
    : null;
  return Boolean((media && media.matches) || (window.navigator && window.navigator.standalone));
}

function isIosDevice() {
  if (typeof window === 'undefined' || !window.navigator) return false;
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || '');
}

/**
 * Instalación de la PWA (brief requisito 4):
 * - Android/Chrome dispara `beforeinstallprompt`; lo capturamos y ofrecemos
 *   el botón "Instalar app". Aceptar u ocultar suprime el aviso 14 días.
 * - iPhone (Safari iOS) nunca dispara ese evento; se detecta por user agent
 *   y se muestra la tarjeta de instrucciones en su lugar, sin supresión (el
 *   brief no la pide para esa rama).
 * - Ya instalada (`display-mode: standalone` o `navigator.standalone`): nada.
 */
export function useInstallPrompt() {
  const [standalone] = useState(isStandaloneDisplay);
  const [isIos] = useState(isIosDevice);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [suppressed, setSuppressed] = useState(() => Date.now() < readSuppressedUntil());

  useEffect(() => {
    if (standalone) return undefined;
    function handleBeforeInstallPrompt(event) {
      event.preventDefault();
      setDeferredPrompt(event);
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, [standalone]);

  const suppress = useCallback(() => {
    writeSuppressedUntil(Date.now() + SUPPRESS_MS);
    setSuppressed(true);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
      suppress();
    }
  }, [deferredPrompt, suppress]);

  const dismiss = useCallback(() => {
    setDeferredPrompt(null);
    suppress();
  }, [suppress]);

  return {
    canInstall: !standalone && !suppressed && Boolean(deferredPrompt),
    showIosCard: !standalone && isIos && !deferredPrompt,
    promptInstall,
    dismiss,
  };
}
